import { api } from "../base";
import type {
  IHarukoGetLoanV2Response,
  IHarukoGetLoansV2Response,
  IHarukoListVenueAccounts,
  IHarukoListVenueAccountsResultGroups,
  IHarukoLoanV2,
} from "@pulse-ui/codegen-v2/generated_ts/venues/haruko";

export type {
  IHarukoLoanV2,
  IHarukoLoanV2AdditionalDataFields,
  IHarukoLoanV2Collateral,
  IHarukoLoanV2CollateralEntries,
  IHarukoLoanV2InterestRates,
  IHarukoLoanV2Thresholds,
  IHarukoLoanV2Valuation,
  IHarukoLoanV2ValuationCollateral,
  IHarukoListVenueAccountsResultGroups,
  IHarukoListVenueAccountsResultGroupsVenues,
} from "@pulse-ui/codegen-v2/generated_ts/venues/haruko";

// The trading-ops mutations accept `{ loan: Partial<IHarukoLoanV2> }` — the
// Rust handler uses serde Option<T> for every field, so missing keys are
// tolerated and the wizard doesn't need to populate everything.
export interface IHarukoCreateLoanRequest {
  loan: Partial<IHarukoLoanV2>;
}

export interface IHarukoUpdateLoanRequest {
  loan: Partial<IHarukoLoanV2>;
}

// Hand-written to match the trading-ops passthrough shape; nullability
// mirrors haruko.yml's haruko_account_metadata schema (all fields optional).
export interface IHarukoCategorisedTag {
  category: string | null;
  tag: string | null;
}

export interface IHarukoAccountMetadata {
  venueAccountId: number | null;
  categorisedTags: IHarukoCategorisedTag[] | null;
}

interface IHarukoAccountMetadataResponse {
  status: string;
  result: { accountMetadata: IHarukoAccountMetadata[] };
}

const loansEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    getHarukoLoans: build.query<IHarukoLoanV2[], void>({
      query: () => ({
        url: "/api/trading-ops/v1/haruko-loans",
      }),
      transformResponse: (response: IHarukoGetLoansV2Response) =>
        response?.result?.loans ?? [],
      providesTags: ["HarukoLoans"],
    }),

    getHarukoLoan: build.query<IHarukoLoanV2 | null, string>({
      query: (loanId) => ({
        url: "/api/trading-ops/v1/haruko-loan",
        params: { loanId },
      }),
      transformResponse: (response: IHarukoGetLoanV2Response) =>
        response?.result?.loan ?? null,
      providesTags: (_result, _error, loanId) => [
        { type: "HarukoLoans", id: loanId },
      ],
    }),

    getHarukoVenueAccounts: build.query<
      IHarukoListVenueAccountsResultGroups[],
      void
    >({
      query: () => ({
        url: "/api/trading-ops/v1/haruko-venue-accounts",
      }),
      transformResponse: (response: IHarukoListVenueAccounts) =>
        response?.result?.groups ?? [],
    }),

    getHarukoAccountMetadata: build.query<IHarukoAccountMetadata[], void>({
      query: () => ({
        url: "/api/trading-ops/v1/haruko-account-metadata",
      }),
      transformResponse: (response: IHarukoAccountMetadataResponse) =>
        response?.result?.accountMetadata ?? [],
    }),

    createHarukoLoan: build.mutation<
      IHarukoLoanV2 | null,
      IHarukoCreateLoanRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/haruko-loan",
        method: "POST",
        body,
      }),
      transformResponse: (response: IHarukoGetLoanV2Response) =>
        response?.result?.loan ?? null,
      invalidatesTags: ["HarukoLoans"],
    }),

    updateHarukoLoan: build.mutation<
      IHarukoLoanV2 | null,
      IHarukoUpdateLoanRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/haruko-loan",
        method: "PUT",
        body,
      }),
      transformResponse: (response: IHarukoGetLoanV2Response) =>
        response?.result?.loan ?? null,
      invalidatesTags: (_result, _error, { loan }) =>
        loan.loanId
          ? [{ type: "HarukoLoans", id: loan.loanId }, "HarukoLoans"]
          : ["HarukoLoans"],
    }),
  }),
});

export const {
  useGetHarukoLoansQuery,
  useGetHarukoLoanQuery,
  useLazyGetHarukoLoanQuery,
  useGetHarukoVenueAccountsQuery,
  useGetHarukoAccountMetadataQuery,
  useCreateHarukoLoanMutation,
  useUpdateHarukoLoanMutation,
} = loansEndpoints;
